<template>
  <div class="relative flex flex-col items-center gap-6">
    <div class="flex w-full flex-col gap-1.5">
      <ui-label>{{ tr('Display placement') }}</ui-label>
      <select-field
        v-model="adFormat"
        :options="adFormats"
        :reduce="(item) => item.machine_name"
        :aria-label="tr('Display placement')"
        :disabled="isLoading"
      />
    </div>

    <loader-circle-icon v-if="isLoading" class="mt-8 size-6 animate-spin text-muted-foreground" />
    <p v-else-if="!isLoaded" class="m-0 mt-8 text-sm text-muted-foreground">
      {{ tr('An error occurred while loading the preview.') }}
    </p>

    <!-- The Meta iframe is scaled down with a transform, so the wrapper clips it. -->
    <div
      v-show="isLoaded"
      ref="iframeWrapper"
      class="overflow-hidden [&_iframe]:absolute [&_iframe]:min-h-[700px] [&_iframe]:origin-top-left"
      :style="wrapperStyle"
      v-html="iframe"
    />
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import ReportService from '@/services/Report';
import SelectField from '@/ui/components/SelectField.vue';

export default {
  name: 'AdPreview',
  components: {
    LoaderCircleIcon: LoaderCircle,
    SelectField,
  },
  props: {
    adId: {
      type: [ String, Number ],
      required: true,
    },
    adAccountId: {
      type: String,
      required: true,
    },
    scale: {
      type: Number,
      required: false,
      default: 0.65,
    },
    // Optional share-report tokens; omitted in authenticated contexts.
    tokenPart1: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    tokenPart2: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
  },
  data: () => ({
    adFormat: 'DESKTOP_FEED_STANDARD',
    isLoading: false,
    isLoaded: false,
    iframe: null,
    width: null,
    height: null,
  }),
  computed: {
    adFormats() {
      return this.$store.getters['app/facebookAdFormats'];
    },
    wrapperStyle() {
      if (!this.isLoaded) {
        return { width: '0px', height: '0px' };
      }

      if (!this.height) {
        return { width: 'auto', height: 'auto' };
      }

      return {
        width: (this.width * this.scale) + 'px',
        height: (this.height * this.scale) + 'px',
      };
    },
  },
  watch: {
    adFormat() {
      this.isLoaded = false;
      this.width = null;
      this.height = null;
      this.generate();
    },
  },
  mounted() {
    this.generate();
  },
  methods: {
    generate() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      ReportService.getAdPreview({
        adAccountId: this.adAccountId,
        adId: this.adId,
        adFormat: this.adFormat,
        tokenPart1: this.tokenPart1,
        tokenPart2: this.tokenPart2,
      })
        .then(resp => {
          let iframe = resp.iframe;
          iframe = iframe.replace('style="', 'style="transform: scale(' + this.scale + ');');
          this.iframe = iframe;
          this.width = resp.width;
          this.height = resp.height > 700 ? resp.height : 700; // min-height value in css.

          this.$nextTick(() => this.initIframeOnLoadListener());

          return resp;
        })
        .catch(err => {
          this.isLoading = false;
          this.error(err);
        });
    },
    initIframeOnLoadListener() {
      const wrapper = this.$refs['iframeWrapper'];
      const iframe = wrapper ? wrapper.querySelector('iframe') : null;
      if (iframe) {
        iframe.addEventListener('load', () => this.onLoaded());
      }
    },
    onLoaded() {
      this.isLoaded = true;
      this.isLoading = false;
    },
  },
};
</script>
